import React, { useState, useRef, useEffect } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { cn } from '../../../utils/cn';

const inputVariants = tv({
  base: [
    'flex w-full rounded-xl border theme-border input-surface px-3 py-2 text-sm text-textPrimary',
    'placeholder:text-textMuted',
    'focus:outline-none focus:ring-2 focus:ring-[var(--focus-ring)] focus:border-[var(--focus-ring)]',
    'disabled:cursor-not-allowed disabled:bg-disabledBg disabled:text-disabledText',
    'transition-colors duration-200 ease-in-out',
    'hover:theme-border'
  ],
  variants: {
    variant: {
      default: '',
      error: 'border-danger focus:ring-danger focus:border-danger',
      success: 'border-success focus:ring-success focus:border-success'
    },
    size: {
      default: 'h-10',
      sm: 'h-8 px-2 text-xs',
      lg: 'h-12 px-4 text-base'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});

type SizeVariant = NonNullable<VariantProps<typeof inputVariants>['size']>;
export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: SizeVariant;
  variant?: 'default' | 'error' | 'success';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  onCommit?: (value: string) => void;
  editable?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ 
    className, 
    size, 
    variant = 'default',
    loading = false, 
    icon, 
    iconPosition = 'left', 
    onCommit,
    editable = true,
    value: externalValue,
    onChange: externalOnChange,
    onBlur: externalOnBlur,
    onKeyDown: externalOnKeyDown,
    ...props 
  }, ref) => {
    const [internalValue, setInternalValue] = useState(externalValue);
    const inputRefInternal = useRef<HTMLInputElement>(null);

    // Sync internal value with external value
    useEffect(() => {
      setInternalValue(externalValue);
    }, [externalValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      
      // Special handling for number inputs
      if (props.type === 'number') {
        // Remove leading zeros but preserve single '0'
        let processedValue = newValue;
        if (newValue.length > 1 && newValue.startsWith('0')) {
          processedValue = newValue.replace(/^0+/, '');
          if (processedValue === '') processedValue = '0';
        }
        setInternalValue(processedValue);
      } else {
        setInternalValue(newValue);
      }
      
      if (externalOnChange) {
        externalOnChange(e);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (onCommit && editable) {
        onCommit(String(internalValue || ''));
      }
      
      if (externalOnBlur) {
        externalOnBlur(e);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && onCommit && editable) {
        onCommit(String(internalValue || ''));
        inputRefInternal.current?.blur();
      }
      
      if (e.key === 'Escape' && editable) {
        setInternalValue(externalValue);
        inputRefInternal.current?.blur();
      }
      
      if (externalOnKeyDown) {
        externalOnKeyDown(e);
      }
    };

    // If not editable, render as span
    if (!editable) {
      return (
        <span className={cn(inputVariants({ variant, size, className }), 'cursor-default')}>
          {internalValue}
        </span>
      );
    }

    return (
      <div className="relative">
        {icon && iconPosition === 'left' && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-textMuted">
            {icon}
          </div>
        )}
        <input
          className={cn(
            inputVariants({ variant, size, className }),
            icon && iconPosition === 'left' && 'pl-10',
            icon && iconPosition === 'right' && 'pr-10'
          )}
          ref={(el) => {
            if (typeof ref === 'function') {
              ref(el);
            } else if (ref) {
              ref.current = el;
            }
            inputRefInternal.current = el;
          }}
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          {...props}
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          </div>
        )}
        {icon && iconPosition === 'right' && !loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-textMuted">
            {icon}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input, inputVariants }; 